import type { CSSProperties, ReactNode } from 'react';
import { X } from '@ui/shared/icons';

/**
 * Shared form building blocks for objective create/edit flows (FirstRunSetup,
 * ObjectiveForm). Extracted so the "New objective" and "Edit objective"
 * screens reuse the exact same field/button/row visual language as the
 * first-run flow instead of a parallel one — per the coordinator's explicit
 * "reuse, don't invent" direction. Nothing here is domain-aware; all of it
 * takes plain values/callbacks.
 */

export const inputStyle: CSSProperties = {
  width: '100%',
  minHeight: 'var(--touch-target-min)',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  fontSize: 'var(--type-body-size)',
  fontFamily: 'var(--font-family)',
};

export const validationHelperStyle: CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: 'var(--type-caption-size)',
  marginTop: 'var(--space-2)',
};

export function Field({
  label,
  children,
  as = 'label',
}: {
  readonly label: string;
  readonly children: ReactNode;
  /**
   * `'label'` (default) gives click-to-focus on a single wrapped text
   * input, which is the common case. Fields wrapping a popover/dialog with
   * multiple interactive descendants (e.g. `DatePickerField`) must pass
   * `as="div"` instead: a `<label>` wrapping more than one interactive
   * descendant forwards any click within it to the first one, which on
   * iOS Safari re-fires the popover's own opening trigger on every close
   * tap (Done button, outside-tap scrim) and makes it un-closable.
   */
  readonly as?: 'label' | 'div';
}) {
  const Tag = as;
  return (
    <Tag style={{ display: 'block', marginTop: 'var(--space-4)' }}>
      <span
        style={{
          display: 'block',
          fontSize: 'var(--type-caption-size)',
          color: 'var(--text-muted)',
          marginBottom: 'var(--space-1)',
        }}
      >
        {label}
      </span>
      {children}
    </Tag>
  );
}

export function PrimaryButton({
  onClick,
  disabled,
  children,
}: {
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        minHeight: 'var(--touch-target-min)',
        padding: '0 var(--space-5)',
        borderRadius: 'var(--radius-md)',
        // Vivid tier fill is correct per tokens.md (button fills are a
        // listed vivid-tier use); self-border + shadow so a large filled
        // surface reads as a designed raised element instead of a flat,
        // undifferentiated color block next to the secondary button.
        background: disabled ? 'var(--surface-hover)' : 'var(--hue-primary-vivid)',
        border: disabled
          ? '1px solid var(--border-subtle)'
          : '1px solid var(--hue-primary-strong)',
        boxShadow: disabled ? 'none' : 'var(--shadow-1)',
        color: disabled ? 'var(--text-disabled)' : 'var(--text-on-accent)',
        fontWeight: 600,
        fontSize: 'var(--type-body-size)',
      }}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({
  onClick,
  children,
}: {
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="secondary-button-outline"
      style={{
        minHeight: 'var(--touch-target-min)',
        padding: '0 var(--space-5)',
        borderRadius: 'var(--radius-md)',
        background: 'transparent',
        border: '1px solid var(--border-strong)',
        color: 'var(--text-secondary)',
        fontWeight: 500,
        fontSize: 'var(--type-body-size)',
      }}
    >
      {children}
    </button>
  );
}

/**
 * One action-title row with an inline remove affordance, shared by the
 * onboarding actions step and the objective-edit actions list. The remove
 * button only renders when `onRemove` is passed AND there's more than one
 * row total (`canRemove`) — callers decide the "don't let the last row
 * disappear" policy since onboarding and edit-mode have slightly different
 * rules about what "empty" means once at least one action already exists.
 */
export function ActionRow({
  label,
  value,
  onChange,
  onBlur,
  onKeyDown,
  onRemove,
  canRemove,
  placeholder = 'e.g. Schedule the exam',
  helperText,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly onBlur?: () => void;
  readonly onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  readonly onRemove?: () => void;
  readonly canRemove: boolean;
  readonly placeholder?: string;
  readonly helperText?: ReactNode;
}) {
  return (
    <Field label={label}>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            style={inputStyle}
          />
        </div>
        {onRemove && canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${label}`}
            style={{
              width: 'var(--touch-target-min)',
              height: 'var(--touch-target-min)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              color: 'var(--text-muted)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </div>
      {helperText}
    </Field>
  );
}
